/* eslint-disable max-lines */
/* istanbul ignore file */
/* eslint-disable max-lines */
import * as Sentry from '@sentry/react';
import { Suspense, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromChildren,
  createRoutesFromElements,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import AppVersion from 'common/components/AppVersion';
import ErrorDialog from 'common/errors/ErrorDialog';
import useRoles from 'common/hooks/useRoles';
import AppLayout from 'common/layouts/AppLayout';
import ProtectedRoutes from 'common/routes/ProtectedRoutes';
import RequireAuth from 'common/routes/RequireAuth';
import { appActions } from 'store/slices/appSlice';

import RequestAccess from 'common/components/RequestAccess';
import { DisableInvoiceGroup, EnableInventory } from 'common/utils';
import { JSPrintManager } from 'jsprintmanager';
import AdminCenter from 'modules/AdminCenter';
import BatchShipments from 'modules/BatchShipments';
import CatalogManagement from 'modules/CatalogManagement';
import DrugManagement from 'modules/DrugManagement';
import Events from 'modules/Events';
import CoaManagement from 'modules/CoaManagement';
import ExternalTransferPharmacy from 'modules/ExternalTransferPharmacy';
import FillHistoryNdcUpdate from 'modules/FillHistoryNdcUpdate';
import FulfillingPharmacySettings from 'modules/FulfillingPharmacySettings';
import GenerateInvoices from 'modules/GenerateInvoices';
import InvoiceGroups from 'modules/InvoiceGroups';
import InvoiceGroupInvoices from 'modules/InvoiceGroups/components/InvoiceGroupInvoices';
import InvoiceCreate from 'modules/InvoiceGroups/components/InvoiceCreate';
import InvoiceEdit from 'modules/InvoiceGroups/components/InvoiceEdit';
import ProductPricing from 'modules/ProductPricing';
import InventoryDashboard from 'modules/InventoryDashboard';
import InventoryManagement from 'modules/InventoryManagement';
import ManageReversals from 'modules/ManageReversals';
import ManufacturerManagement from 'modules/ManufacturerManagement';
import CreateOrder from 'modules/NewOrder';
import OrdersList from 'modules/ordersList';
import OrderView from 'modules/OrderView';
import PartnerAndPracticeManagement from 'modules/PartnerAndPracticeManagement';
import PartnerNdcPreference from 'modules/PartnerNdcPreference';
import PatientsList from 'modules/PatientsList';
import PatientView from 'modules/PatientView';
import OrderRequest from 'modules/RequestStatus';
import RxScanStation from 'modules/RxScanStation';
import Shipments from 'modules/Shipments';
import SigManagement from 'modules/SigManagement';
import SignIn from 'modules/SignIn';
import StockManagement from 'modules/StockManagement';
import Transactions from 'modules/Transactions';
import VendorManagement from 'modules/VendorManagement';
import WorkQueue from 'modules/WorkQueue';
import { getFulfillingPharmacies } from 'store/thunks/pharmacyThunks';
import { listLineItemTags } from 'store/thunks/tagThunks';
import packageJson from '../package.json';

Sentry.init({
  sendDefaultPii: true,
  environment: process.env.REACT_APP_ENV,
  enabled: !!process.env.REACT_APP_SENTRY_ENABLED,
  dsn: process.env.REACT_APP_SENTRY_DSN,
  tracesSampleRate: parseInt(process.env.REACT_APP_SENTRY_TRACE_SAMPLE_RATE, 10) || 0,
  normalizeDepth: 10,
  maxValueLength: 1000,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  replaysOnErrorSampleRate: parseInt(process.env.REACT_APP_ENTRY_REPLAY_SAMPLE_RATE, 10) || 0,
  initialScope: {
    tags: {
      version: packageJson.version,
    },
  },
});

const ENV = process.env.NODE_ENV;
const isProd = ENV === 'production';

if (isProd) {
  JSPrintManager.license_url = 'https://www.neodynamic.com/licenses/jspm/v8/PerfectRx';
}

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const App = () => {
  const hasUserAuth = useSelector(({ auth }) => auth?.currentTenantId);

  const dispatch = useDispatch();
  const hasAccess = useRoles();

  const router = useMemo(
    () =>
      sentryCreateBrowserRouter(
        createRoutesFromElements(
          <>
            {/* Unprotected public routes */}
            <Route path='/signin' element={<SignIn />} />

            {/* Protected routes */}
            <Route
              path='/'
              element={
                <ProtectedRoutes>
                  <AppLayout />
                </ProtectedRoutes>
              }
            >
              <Route
                index
                element={
                  <RequireAuth allow={hasAccess.viewWorkQueue}>
                    <WorkQueue />
                  </RequireAuth>
                }
              />
              <Route
                path='/patients'
                element={
                  <RequireAuth allow={hasAccess.viewPatientsList}>
                    <PatientsList />
                  </RequireAuth>
                }
              />
              <Route
                path='/patients/:mpi'
                element={
                  <RequireAuth allow={hasAccess.viewPatientDetails}>
                    <PatientView />
                  </RequireAuth>
                }
              />
              <Route
                path='/orders'
                element={
                  <RequireAuth allow={hasAccess.viewOrdersList}>
                    <OrdersList />
                  </RequireAuth>
                }
              />
              <Route
                path='/orders/:orderId'
                element={
                  <RequireAuth allow={hasAccess.viewOrdersList}>
                    <OrderView />
                  </RequireAuth>
                }
              />

              <Route
                path='/rx-scan'
                element={
                  <RequireAuth allow={hasAccess.rxScanStation}>
                    <RxScanStation />
                  </RequireAuth>
                }
              />

              <Route
                path='/events'
                element={
                  <RequireAuth allow={hasAccess.viewEvents}>
                    <Events />
                  </RequireAuth>
                }
              />
              <Route
                path='/adminCenter'
                element={
                  <RequireAuth allow={hasAccess.viewAdminCenter}>
                    <AdminCenter />
                  </RequireAuth>
                }
              >
                <Route
                  path='/adminCenter/drugManagement'
                  element={
                    <RequireAuth allow={hasAccess.viewDrugManagement}>
                      <DrugManagement />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/adminCenter/catalogManagement'
                  element={
                    <RequireAuth allow={hasAccess.viewCatalogManagement}>
                      <CatalogManagement />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/adminCenter/sigManagement'
                  element={
                    <RequireAuth allow={hasAccess.viewSigManagement}>
                      <SigManagement />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/adminCenter/requestStatus'
                  element={
                    <RequireAuth allow={hasAccess.viewRequestStatus}>
                      <OrderRequest />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/adminCenter/PartnerPracticeManagement'
                  element={
                    <RequireAuth allow={hasAccess.viewPartnerAndPracticeManagement}>
                      <PartnerAndPracticeManagement />
                    </RequireAuth>
                  }
                />
                {/* Same component as /inventory/partner-preferences. Both hosts
                    supply their own heading, so the screen renders unchanged. */}
                <Route
                  path='/adminCenter/partnerPreferences'
                  element={
                    <RequireAuth allow={hasAccess.viewPartnerNdcPreference}>
                      <PartnerNdcPreference />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/adminCenter/facilitySettings'
                  element={
                    <RequireAuth allow={hasAccess.viewFacilityShippingDefault}>
                      <FulfillingPharmacySettings />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/adminCenter/externalPharmacy'
                  element={
                    <RequireAuth allow={hasAccess.viewExternalTransferPharmacy}>
                      <ExternalTransferPharmacy />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/adminCenter/invoiceGroups'
                  element={
                    <RequireAuth allow={!DisableInvoiceGroup && hasAccess.viewInvoiceGroups}>
                      <InvoiceGroups />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/adminCenter/invoiceGroups/:invoiceGroupId'
                  element={
                    <RequireAuth allow={!DisableInvoiceGroup && hasAccess.viewInvoiceGroups}>
                      <InvoiceGroupInvoices />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/adminCenter/invoiceGroups/:invoiceGroupId/invoices/new'
                  element={
                    <RequireAuth allow={!DisableInvoiceGroup && hasAccess.viewInvoiceGroups}>
                      <InvoiceCreate />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/adminCenter/invoiceGroups/:invoiceGroupId/invoices/:invoiceId/edit'
                  element={
                    <RequireAuth allow={!DisableInvoiceGroup && hasAccess.viewInvoiceGroups}>
                      <InvoiceEdit />
                    </RequireAuth>
                  }
                />
                <Route
                  path='/adminCenter/productPricing'
                  element={
                    <RequireAuth allow={hasAccess.viewProductPricing}>
                      <ProductPricing />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route
                path='/adminCenter/bulkNdcUpdate'
                element={
                  <RequireAuth allow={hasAccess.bulkFillHistoryUpdate}>
                    <FillHistoryNdcUpdate />
                  </RequireAuth>
                }
              />
              <Route
                path='/adminCenter/generateInvoices'
                element={
                  <RequireAuth allow={hasAccess.generateInvoices}>
                    <GenerateInvoices />
                  </RequireAuth>
                }
              />
              <Route
                path='/coa'
                element={
                  <RequireAuth allow={hasAccess.viewCoa}>
                    <CoaManagement />
                  </RequireAuth>
                }
              />
              <Route
                path='/transactions'
                element={
                  <RequireAuth allow={hasAccess.viewPaymentTransactions}>
                    <Transactions />
                  </RequireAuth>
                }
              />
              <Route
                path='/shipments'
                element={
                  <RequireAuth allow={hasAccess.viewShipments}>
                    <Shipments />
                  </RequireAuth>
                }
              />
              <Route
                path='/batchShipments'
                element={
                  <RequireAuth allow={hasAccess.viewBatchShipments}>
                    <BatchShipments />
                  </RequireAuth>
                }
              />
              <Route
                path='/createOrder'
                element={
                  <RequireAuth allow={hasAccess.createOrder}>
                    <CreateOrder />
                  </RequireAuth>
                }
              />

              {EnableInventory && (
                <Route
                  path='/inventory'
                  element={
                    <RequireAuth allow={hasAccess.viewInventoryModule}>
                      <InventoryManagement />
                    </RequireAuth>
                  }
                >
                  <Route
                    path='/inventory/stockManagement'
                    element={
                      <RequireAuth allow={hasAccess.viewStockManagement}>
                        <StockManagement />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path='/inventory/dashboard'
                    element={
                      <RequireAuth allow={hasAccess.viewInventoryModule}>
                        <InventoryDashboard />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path='/inventory/vendors'
                    element={
                      <RequireAuth allow={hasAccess.viewVendorManagement}>
                        <VendorManagement />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path='/inventory/manufacturers'
                    element={
                      <RequireAuth allow={hasAccess.viewManufacturerManagement}>
                        <ManufacturerManagement />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path='/inventory/manage-reversals'
                    element={
                      <RequireAuth allow={hasAccess.viewReversalReview}>
                        <ManageReversals />
                      </RequireAuth>
                    }
                  />
                  {/* POM-5974 — parked alongside the Inventory tab entry. The
                      screen lives at /adminCenter/partnerPreferences for now. */}
                </Route>
              )}
              <Route
                path='/request-access'
                element={
                  <ProtectedRoutes>
                    <RequestAccess />
                  </ProtectedRoutes>
                }
              />

              <Route path='*' element={<div>Not Found </div>} />
            </Route>
          </>
        )
      ),
    [hasAccess]
  );

  useEffect(() => {
    fetch('https://geolocation-db.com/json/')
      .then((data) => data.json())
      .then(({ IPv4, ...location }) =>
        dispatch(
          appActions.updateGeolocation({
            ipAddress: IPv4,
            geolocation: JSON.stringify({ ...location }),
          })
        )
      )
      .catch(() => ({}));

    if (hasUserAuth) {
      dispatch(getFulfillingPharmacies());
      dispatch(listLineItemTags());
    }
  }, [dispatch, hasUserAuth]);

  return (
    <>
      <AppVersion />
      <ErrorDialog />
      {/* This is a global level suspense, thats why it's intentional to be blank div at this time */}
      <Suspense fallback={<div />}>
        <RouterProvider router={router} />
      </Suspense>
    </>
  );
};

export default App;
